/**
 * Ted #28 Phase 2 (#1050) — the DEPLOY-WINDOW shim, and nothing else.
 *
 * Frontends auto-deploy on merge; the server does not (Johnny runs it). For
 * that window a Phase 1 server still answers with the legacy fields the
 * retired private chips used to read — `vax.level` on the card, `unpaid` /
 * `balanceDueCents` / `walletBalanceCents` / `hasMedications` on the row —
 * and a `flags` list that carries transport only. Without this shim the desk
 * would lose the `$` and the vax badge for the length of that window.
 *
 * So: when the server's list does not carry a key but the legacy field says
 * the flag is true, synthesize it HERE, once, and hand the ONE renderer the
 * merged list. The rule is narrow on purpose — a current server ALWAYS emits
 * `vax` when `vax.level !== 'ok'`, and `visit_unpaid` from the same row
 * balance under the same money gate as `unpaid`, so the shim can never double
 * a pill or contradict the registry; it only fills a hole an older server left.
 *
 * `visit_unpaid` (Johnny, 2026-09-23, "both flags for the $") is the visit's
 * own `$`. It used to be synthesized here as `balance`, which on a Phase 2
 * server is the OWNER's open invoices — a different question — so a row whose
 * owner owed elsewhere hid this visit's amount.
 *
 * DELETE this file (and its two call sites) once the Phase 2 server is in
 * prod and the handoff says so. It carries no truth of its own.
 */
import type { DogCard, DogFlag } from '../../lib/api.js';

export interface LegacyRowFacts {
  unpaid?: boolean;
  balanceDueCents?: number;
  storeCreditCents?: number;
  hasMedications?: boolean;
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

const VAX_LABEL: Record<Exclude<DogCard['vax']['level'], 'ok'>, string> = {
  expired: 'Vax expired',
  expiring: 'Vax expiring',
  none: 'No vax records',
};

/** The server's flags, plus what an older server only said the legacy way. */
export function resolveDogFlags(
  dog: Pick<DogCard, 'vax' | 'flags'>,
  legacy: LegacyRowFacts = {},
): DogFlag[] {
  const flags: DogFlag[] = Array.isArray(dog.flags) ? [...dog.flags] : [];
  const has = (key: string): boolean => flags.some((f) => f.key === key);

  if (!has('vax') && dog.vax && dog.vax.level !== 'ok') {
    flags.push({
      key: 'vax',
      scope: 'dog',
      severity: dog.vax.level === 'expired' ? 'danger' : 'warn',
      label: VAX_LABEL[dog.vax.level],
      detail: dog.vax.detail,
      requiresArrivalCheck: false,
      alertAt: ['creation', 'checkin'],
    });
  }
  if (!has('visit_unpaid') && legacy.unpaid) {
    const cents = typeof legacy.balanceDueCents === 'number' && legacy.balanceDueCents > 0 ? legacy.balanceDueCents : null;
    flags.push({
      key: 'visit_unpaid',
      scope: 'account',
      severity: 'danger',
      label: cents === null ? 'This visit unpaid' : `This visit unpaid ${money(cents)}`,
      detail:
        cents === null
          ? 'This visit has an unsettled balance — collect it at checkout.'
          : `This visit has ${money(cents)} on open invoices — collect it at checkout.`,
      requiresArrivalCheck: false,
      alertAt: ['checkout'],
    });
  }
  if (!has('credit') && typeof legacy.storeCreditCents === 'number' && Number.isFinite(legacy.storeCreditCents) && legacy.storeCreditCents > 0) {
    flags.push({
      key: 'credit',
      scope: 'account',
      severity: 'info',
      label: `Credit ${money(legacy.storeCreditCents)}`,
      detail: `Store credit ${money(legacy.storeCreditCents)} available — apply it at checkout.`,
      requiresArrivalCheck: false,
      alertAt: ['checkout'],
    });
  }
  if (!has('meds') && legacy.hasMedications === true) {
    flags.push({
      key: 'meds',
      scope: 'dog',
      severity: 'info',
      label: 'Meds',
      detail: 'Has medications — see the run card for the protocol.',
      requiresArrivalCheck: false,
      alertAt: ['checkin'],
    });
  }
  return flags;
}
